import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaSpinner,
  FaEye,
  FaTrash,
  FaCheck,
  FaTimes,
  FaFilePdf,
  FaCalendarAlt,
  FaBook,
  FaEdit,
  FaPlus,
  FaTimesCircle,
  FaCheckCircle,
  FaUpload,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import UploadPYQModal from "./UploadPYQModal";
import { buildApiUrl } from "../../utils/apiConfig";

const PYQManagement = ({ isDarkMode }) => {
  const [pyqs, setPyqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingPyq, setEditingPyq] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    examYear: "",
    examType: "External",
    status: "approved",
  });

  const examTypes = ["Internal", "External", "CIE", "SEE"];

  useEffect(() => {
    fetchPYQs();
  }, []);

  const fetchPYQs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/api/pyqs/admin/all",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPyqs(response.data);
    } catch (error) {
      console.error("Fetch PYQs error:", error);
      toast.error("Failed to fetch PYQs");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/pyqs/${id}/status`,
        { status },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(`PYQ ${status}`);
      fetchPYQs();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deletePYQ = async (id) => {
    if (!window.confirm("Delete this PYQ?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/pyqs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("PYQ deleted");
      fetchPYQs();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const updatePYQ = async () => {
    if (!formData.title || !formData.examYear) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/pyqs/${editingPyq._id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("PYQ updated");
      setShowModal(false);
      setEditingPyq(null);
      fetchPYQs();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const filteredPYQs = pyqs.filter(
    (p) => filter === "all" || p.status === filter,
  );

  const getBgColor = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
      </div>
    );

  return (
    <div>
      {/* Header with Upload Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <h3 style={{ color: getTextColor(), margin: 0 }}>
          Manage Previous Year Question Papers
        </h3>
        <Link to="/admin/upload-pyq">
          <button
            style={{
              padding: "0.6rem 1.2rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontWeight: "500",
              fontSize: "0.85rem",
            }}
          >
            <FaUpload size={14} /> Upload New PYQ
          </button>
        </Link>
      </div>

      {/* Filter Buttons */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setFilter("all")}
          style={{
            padding: "0.5rem 1rem",
            background: filter === "all" ? "#8b5cf6" : getBgColor(),
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "8px",
            cursor: "pointer",
            color: getTextColor(),
          }}
        >
          All ({pyqs.length})
        </button>
        <button
          onClick={() => setFilter("approved")}
          style={{
            padding: "0.5rem 1rem",
            background: filter === "approved" ? "#10b981" : getBgColor(),
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "8px",
            cursor: "pointer",
            color: getTextColor(),
          }}
        >
          Approved ({pyqs.filter((p) => p.status === "approved").length})
        </button>
        <button
          onClick={() => setFilter("pending")}
          style={{
            padding: "0.5rem 1rem",
            background: filter === "pending" ? "#f59e0b" : getBgColor(),
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "8px",
            cursor: "pointer",
            color: getTextColor(),
          }}
        >
          Pending ({pyqs.filter((p) => p.status === "pending").length})
        </button>
        <button
          onClick={() => setFilter("rejected")}
          style={{
            padding: "0.5rem 1rem",
            background: filter === "rejected" ? "#ef4444" : getBgColor(),
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "8px",
            cursor: "pointer",
            color: getTextColor(),
          }}
        >
          Rejected ({pyqs.filter((p) => p.status === "rejected").length})
        </button>
      </div>

      {filteredPYQs.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: getBgColor(),
            borderRadius: "16px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <FaFilePdf size={48} color={getTextColor()} opacity={0.5} />
          <p style={{ color: getTextColor(), marginTop: "1rem" }}>
            No PYQs found
          </p>
          <button
            onClick={() => {
              console.log("Upload button clicked from empty state");
              setShowUploadModal(true);
            }}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              background: "#8b5cf6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            <FaPlus /> Upload First PYQ
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {filteredPYQs.map((pyq) => (
            <motion.div
              key={pyq._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                background: getBgColor(),
                borderRadius: "16px",
                padding: "1rem",
                border: `1px solid ${getBorderColor()}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <FaFilePdf size={32} color="#ef4444" />
                <div>
                  <h4 style={{ color: getTextColor(), margin: 0 }}>
                    {pyq.title}
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.8rem",
                      marginTop: "0.3rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontSize: "0.7rem", color: "#8b5cf6" }}>
                      {pyq.subjectCode}
                    </span>
                    <span style={{ fontSize: "0.7rem", color: getTextColor() }}>
                      <FaBook /> {pyq.branch}
                    </span>
                    <span style={{ fontSize: "0.7rem", color: getTextColor() }}>
                      <FaCalendarAlt /> {pyq.examYear}
                    </span>
                    <span style={{ fontSize: "0.7rem", color: getTextColor() }}>
                      {pyq.examType}
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    padding: "0.2rem 0.6rem",
                    borderRadius: "20px",
                    fontSize: "0.7rem",
                    background:
                      pyq.status === "approved"
                        ? "#10b981"
                        : pyq.status === "pending"
                          ? "#f59e0b"
                          : "#ef4444",
                    color: "white",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.2rem",
                  }}
                >
                  {pyq.status === "approved" ? (
                    <FaCheckCircle size={10} />
                  ) : pyq.status === "pending" ? (
                    <FaClock size={10} />
                  ) : (
                    <FaTimesCircle size={10} />
                  )}
                  {pyq.status}
                </span>
                {pyq.status !== "approved" && (
                  <button
                    onClick={() => updateStatus(pyq._id, "approved")}
                    style={{
                      padding: "0.3rem 0.6rem",
                      background: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.2rem",
                    }}
                  >
                    <FaCheck /> Approve
                  </button>
                )}
                {pyq.status !== "rejected" && (
                  <button
                    onClick={() => updateStatus(pyq._id, "rejected")}
                    style={{
                      padding: "0.3rem 0.6rem",
                      background: "#ef4444",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.2rem",
                    }}
                  >
                    <FaTimes /> Reject
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditingPyq(pyq);
                    setFormData({
                      title: pyq.title,
                      description: pyq.description || "",
                      examYear: pyq.examYear,
                      examType: pyq.examType,
                      status: pyq.status,
                    });
                    setShowModal(true);
                  }}
                  style={{
                    padding: "0.3rem 0.6rem",
                    background: "#8b5cf6",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  <FaEdit /> Edit
                </button>
                <button
                  onClick={() => window.open(buildApiUrl(`/api/pyqs/${pyq._id}`), "_blank")}
                  style={{
                    padding: "0.3rem 0.6rem",
                    background: "#06b6d4",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  <FaEye /> View
                </button>
                <button
                  onClick={() => deletePYQ(pyq._id)}
                  style={{
                    padding: "0.3rem 0.6rem",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {showModal && editingPyq && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: getBgColor(),
              borderRadius: "20px",
              maxWidth: "500px",
              width: "90%",
              padding: "1.5rem",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: getTextColor(), marginBottom: "1rem" }}>
              Edit PYQ
            </h3>
            <input
              type="text"
              placeholder="Title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "1rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <textarea
              placeholder="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="3"
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "1rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <input
              type="text"
              placeholder="Exam Year"
              value={formData.examYear}
              onChange={(e) =>
                setFormData({ ...formData, examYear: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "1rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            />
            <select
              value={formData.examType}
              onChange={(e) =>
                setFormData({ ...formData, examType: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "1rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            >
              {examTypes.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              style={{
                width: "100%",
                padding: "0.8rem",
                marginBottom: "1rem",
                border: `1px solid ${getBorderColor()}`,
                borderRadius: "8px",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            >
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={updatePYQ}
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "#8b5cf6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadPYQModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={fetchPYQs}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default PYQManagement;

